import { parseImportString, parseNamedImports } from '@ms-cloudpack/path-string-parsing';
import { parse, type ImportSpecifier, type ExportSpecifier, init } from 'es-module-lexer';
import fsPromises from 'fs/promises';
import { SourceMapConsumer, type RawSourceMap } from 'source-map';
import path from 'path';
import { normalizedPathRelativeTo } from '@ms-cloudpack/path-utilities';
import type { ConsumedImport, DynamicImport } from '../types/BundleInfo.js';

let initialized = false;

/**
 * Get info about imports and exports from a file.
 * Returns undefined if the file doesn't exist or can't be parsed.
 */
export async function getImportsAndExports(
  filePath: string,
  packagePath?: string,
): Promise<
  | {
      exportNames: Set<string>;
      imports: ConsumedImport[];
      /** Dynamic (async) imports with non-literal paths consumed by this file. */
      dynamicImports: DynamicImport[];
    }
  | undefined
> {
  let source: string;
  try {
    source = await fsPromises.readFile(filePath, 'utf-8');
  } catch (err) {
    // This could happen if a package is missing a file.
    console.warn(`Failed to read ${filePath}:`, err);
    return;
  }

  if (!initialized) {
    // Await init once for the Web Assembly boot to call parse synchronously.
    await init;
    initialized = true;
  }

  let rawImports: readonly ImportSpecifier[];
  let rawExports: readonly ExportSpecifier[];
  try {
    [rawImports, rawExports] = parse(source);
  } catch (err) {
    // This could happen if the source file isn't valid JS
    console.warn(`Failed to parse ${filePath}: ${err}\nSource is:\n${source}`);
    return;
  }

  // es-module-lexer doesn't handle export * from statements correctly as it parses them as
  // imports but not as exports. We need to handle them separately.
  // TODO: Remove when https://github.com/guybedford/es-module-lexer/issues/76 is fixed
  const exportStarPaths: string[] = [];

  const imports: ConsumedImport[] = [];
  const dynamicImports: DynamicImport[] = [];
  for (const imprt of new Set(rawImports)) {
    if (imprt.d === -2) {
      continue; // -2 means import.meta, which we don't care about
    }

    // es-module-lexer returns objects where the "n" property is the import source,
    // e.g. "@fluentui/react/lib/Button" for this import:
    //   import { Button } from '@fluentui/react/lib/Button';
    // However, "n" will be undefined for import() if isn't a plain string literal.
    let importPath = imprt.n;
    // ss and se point to the entire import string's start/end
    const importCode = source.slice(imprt.ss, imprt.se);
    // s and e point to the specificer's start/end, including quotes -- e.g. "@fluentui/react/lib/Button"
    const importSpecifier = source.slice(imprt.s, imprt.e);

    // Currently when the import source is a backtick string, the path ("n" property) is undefined.
    // This is a limitation in es-module-lexer.
    if (/^`[^$`]+`$/.test(importSpecifier)) {
      // If it's a static string which happened to use backticks for some reason, we can parse that.
      importPath = importSpecifier.slice(1, -1);
    }

    // Handle literal paths first
    if (importPath) {
      // Handling `export * from` import statements
      if (/export\s*\*\s*from/.test(importCode)) {
        exportStarPaths.push(importPath);
      }

      let importedNames: string[];
      if (imprt.d >= 0) {
        // This is a dynamic import. In this case we don't know which names are used, so assume all of them.
        importedNames = ['*'];
      } else {
        // Parse the import string into its component parts.
        importedNames = parseNamedImports(importCode);
      }

      imports.push({
        ...parseImportString(importPath),
        names: Array.from(new Set(importedNames)),
      });
    } else if (packagePath) {
      // Non-literal path.
      // Find the original source of the import.
      let rawSourceMap: RawSourceMap;
      let originalSource: string | null = null;
      try {
        rawSourceMap = JSON.parse(await fsPromises.readFile(`${filePath}.map`, 'utf-8')) as RawSourceMap;
        const consumer = await new SourceMapConsumer(rawSourceMap);
        const pos = getLineAndColumn(source, imprt.s);
        originalSource = consumer.originalPositionFor(pos).source;
        consumer.destroy(); // Clean up the consumer
        if (originalSource === null) {
          console.debug(`Failed to find original source for import:\nFilename:${filePath}\nImport:\n  ${importCode}`);
        }
      } catch (err) {
        console.debug(`Failed to read sourcemap for import:\nFilename:${filePath}\nImport:\n  ${importCode}`, err);
      }

      // Original source where the import was found.
      const absoluteImportSpecifier = originalSource ? path.resolve(path.dirname(filePath), originalSource) : filePath;

      // TODO: This will give a bad path for a lot of template or concatenated strings
      let packageName = '.'; // Default to local package
      // Remove leading quote from the import specifier if it exists.
      const cleanImportSpecifier = importSpecifier.replace(/^["'`]/, '');
      if (!cleanImportSpecifier.startsWith('.')) {
        packageName = parseImportString(cleanImportSpecifier).packageName;
      }
      dynamicImports.push({
        packageName,
        // Relative path from package path to file with import. Prefer original source if available.
        // or use the file path if original source is not available.
        importOrigin: normalizedPathRelativeTo(packagePath, absoluteImportSpecifier),
        importSpecifier,
      });
    } else {
      console.debug(`Found an import that didn't have a static path:\nFilename:${filePath}\nImport:\n  ${importCode}`);
    }
  }

  // Processing exports is cheap as we already have to parse the source.
  // Helps find exports uncaught by output files.
  const exportNames = new Set<string>();
  for (const exprt of rawExports) {
    if (exprt.n) {
      // Exports don't need to be parsed as it is
      // already in the format we need.
      exportNames.add(exprt.n);
    } else {
      console.debug(
        `Found an export that didn't parse correctly:\nFilename: ${filePath}\nExport:\n  ${source.slice(exprt.s, exprt.e)}`,
      );
    }
  }

  for (const exportPath of exportStarPaths) {
    // This follows the following convention: https://github.com/guybedford/es-module-lexer/issues/76#issuecomment-868561807
    exportNames.add(`*${exportPath}`);
  }

  return { exportNames, imports, dynamicImports };
}

function getLineAndColumn(source: string, position: number): { line: number; column: number } {
  let line = 1;
  let column = 1;

  for (let i = 0; i < position; i++) {
    if (source[i] === '\n') {
      line++;
      column = 1;
    } else {
      column++;
    }
  }

  return { line, column };
}
